import React, { useState } from "react";

import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Collapse,
  Grid,
  Link,
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import TableBody from "@mui/material/TableBody";
import Typography from "@mui/material/Typography";

import { useConfig } from "../../contexts/ConfigContext";
import { TaskRecord } from "../../services/api/Jobs";
import { getDuration, getLocalTime, getRelativeTime } from "../../services/utils/dateTime";
import { getFilename } from "../../services/utils/misc";
import { parseTaskStatus } from "../../services/utils/status";
import ReadOnlyTextField from "../shared/ReadOnlyTextField";
import { VideoPlayerModal } from "../shared/VideoPlayer";
import TaskStatus from "./TaskStatus";

const TasksTable: React.FC<{ tasks: TaskRecord[] }> = ({ tasks }) => {
  const { config } = useConfig();
  const [expanded, setExpanded] = useState<string[]>([]);
  const [previewURL, setPreviewURL] = useState<string | null>(null);

  const handleDetailsClick = (id: string) => {
    setExpanded((prevExpanded) =>
      prevExpanded.includes(id) ? prevExpanded.filter((x) => x !== id) : [...prevExpanded, id],
    );
  };

  const taskColumns = [
    { id: "id", label: "ID" },
    { id: "template", label: "Template" },
    { id: "submit_time", label: "Submit Time" },
    { id: "version", label: "Version" },
    { id: "status", label: "Status" },
    { id: "details", label: "Details" },
  ];

  const taskDetailColumns = [
    { id: "filename", label: "File Name", display: true },
    { id: "playback_type", label: "Playback Type", display: true },
    { id: "url", label: "URL", display: true },
    { id: "primary_regions", label: `${config.primaryAccountName} Regions`, display: true },
    {
      id: "secondary_regions",
      label: `${config.secondaryAccountName} Regions`,
      display: config.secondaryAccountName,
    },
  ];

  const handlePreview = (registeredURL: string) => {
    setPreviewURL(registeredURL);
  };

  const handleHidePreview = () => {
    setPreviewURL(null);
  };

  const shortenPath = (path: string) => {
    const regex = /\/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\/.*)$/;

    // Match the regex pattern against the filename
    const match = path.match(regex);

    // If a match is found, return the first capture group, otherwise return the input path
    return match ? match[1] : path;
  };

  return (
    <Box>
      <Typography variant="h5">Tasks</Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {taskColumns.map((column) => (
                <TableCell key={column.id} sx={{ fontWeight: "bold" }}>
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.map((row) => (
              <React.Fragment key={row._id}>
                <TableRow>
                  <TableCell>{row._id}</TableCell>
                  <TableCell>{row.template}</TableCell>
                  <TableCell>
                    <Tooltip title={getLocalTime(row.submit_time)}>
                      <span>{getRelativeTime(row.submit_time)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{row.version}</TableCell>
                  <TableCell>
                    <TaskStatus status={parseTaskStatus(row.status)} errorType={row.error_type} />
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="contained"
                      color="inherit"
                      onClick={() => handleDetailsClick(row._id)}
                    >
                      Details
                    </Button>
                  </TableCell>
                </TableRow>
                {expanded.includes(row._id) && (
                  <TableRow>
                    <TableCell colSpan={6} style={{ padding: 0 }}>
                      <Collapse in={expanded.includes(row._id)} timeout="auto" unmountOnExit>
                        <Card sx={{ margin: 0, border: "none", boxShadow: "none" }}>
                          <CardHeader
                            title={`Encode ${row.encode_id}`}
                            subheader={getDuration(row.start_time, row.finish_time)}
                          ></CardHeader>
                          <CardContent>
                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <ReadOnlyTextField
                                  label="Start Time"
                                  name="Start Time"
                                  value={getLocalTime(row.start_time) || " "}
                                  required
                                  fullWidth
                                />
                              </Grid>
                              <Grid item xs={6}>
                                <ReadOnlyTextField
                                  label="End Time"
                                  name="End Time"
                                  value={getLocalTime(row.finish_time) || " "}
                                  required
                                  fullWidth
                                />
                              </Grid>
                            </Grid>
                            <TableContainer>
                              <Table title="Task Details Table">
                                <TableHead title="Task Details Table Headers">
                                  <TableRow>
                                    {taskDetailColumns.map((column) =>
                                      column.display ? (
                                        <TableCell
                                          key={column.id}
                                          sx={{ fontWeight: "bold" }}
                                          title={column.label}
                                        >
                                          {column.label}
                                        </TableCell>
                                      ) : null,
                                    )}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {row.renditions?.map((rendition) => (
                                    <React.Fragment key={rendition.filename}>
                                      <TableRow>
                                        <TableCell title="Filename value">
                                          <Link href={rendition.registered_url}>
                                            {shortenPath(rendition.filename)}
                                          </Link>
                                        </TableCell>
                                        <TableCell title="Playback type value">
                                          <Chip
                                            size="small"
                                            label={rendition.playback_type.toUpperCase()}
                                            color="primary"
                                            sx={{ minWidth: "60px" }}
                                          />
                                        </TableCell>
                                        <TableCell title="Registered URL value">
                                          {rendition.registered_url && (
                                            <div style={{ display: "flex", alignItems: "center" }}>
                                              <Link href={rendition.registered_url}>
                                                {getFilename(rendition.registered_url)}
                                              </Link>
                                              <PlayArrowIcon
                                                sx={{ paddingLeft: "8px", cursor: "pointer" }}
                                                onClick={() => handlePreview(rendition.registered_url)}
                                              />
                                            </div>
                                          )}
                                        </TableCell>
                                        <TableCell title="Primary Registered Regions value">
                                          {rendition.primary_registered_regions?.map((region) => (
                                            <Chip
                                              key={region}
                                              size="small"
                                              label={region}
                                              sx={{ marginRight: "8px" }}
                                            />
                                          ))}
                                        </TableCell>
                                        {config.secondaryAccountName && (
                                          <TableCell title="Secondary Registered Regions value">
                                            {rendition.secondary_registered_regions?.map((region) => (
                                              <Chip
                                                key={region}
                                                size="small"
                                                label={region}
                                                sx={{ marginRight: "8px" }}
                                              />
                                            ))}
                                          </TableCell>
                                        )}
                                      </TableRow>
                                    </React.Fragment>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </CardContent>
                        </Card>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <VideoPlayerModal title="Preview" videoUrl={previewURL!} onClose={handleHidePreview} />
    </Box>
  );
};

export default TasksTable;
